import { useCallback, useEffect, useRef, useState } from 'react';

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
export const AUTOSAVE_DELAY = 600;

// Serialize writes and keep edits made while a previous version is being saved.
export default function useLessonAutosave(saved, options) {
  const [state, setState] = useState({ base: saved, draft: saved, saving: false, error: '', savedOnce: false });
  const current = useRef(state);
  const config = useRef(options);
  const inFlight = useRef(false);
  current.current = state;
  config.current = options;
  const dirty = !same(state.draft, state.base);

  useEffect(() => {
    setState(value => value.saving || !same(value.draft, value.base) || same(saved, value.base)
      ? value : { ...value, base: saved, draft: saved, error: '' });
  }, [saved]);

  const setDraft = useCallback(update => setState(value => {
    const draft = typeof update === 'function' ? update(value.draft) : update;
    return { ...value, draft, error: same(draft, value.base) ? '' : value.error };
  }), []);
  const reset = useCallback(value => setState({ base: value, draft: value, saving: false, error: '', savedOnce: false }), []);
  const save = useCallback(async (retry = false) => {
    const snapshot = current.current;
    const { persist, onSaved, isValid, blocked } = config.current;
    if (inFlight.current || blocked) return false;
    if (same(snapshot.draft, snapshot.base)) return true;
    if ((!retry && snapshot.error) || !isValid()) return false;
    inFlight.current = true;
    setState(value => ({ ...value, saving: true, error: '' }));
    try {
      const result = await persist(snapshot.draft, snapshot.base);
      setState(value => {
        const draft = { ...result };
        for (const key of Object.keys(value.draft)) {
          if (!same(value.draft[key], snapshot.draft[key])) draft[key] = value.draft[key];
        }
        return { base: result, draft, saving: false, error: '', savedOnce: true };
      });
      onSaved(result);
      return true;
    } catch (error) {
      setState(value => ({ ...value, saving: false, error: error.message || 'Could not save changes. Please retry.' }));
      return false;
    } finally { inFlight.current = false; }
  }, []);

  useEffect(() => {
    if (!dirty || state.saving || state.error || options.blocked) return;
    const timer = setTimeout(() => { save(); }, AUTOSAVE_DELAY);
    return () => clearTimeout(timer);
  }, [dirty, state.draft, state.saving, state.error, options.blocked, save]);
  useEffect(() => {
    if (!dirty && !state.saving) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, state.saving]);

  return { ...state, dirty, setDraft, reset, save };
}
